import { useCallback, useMemo, useState } from 'react';
import type { ReferralAgent } from '@/common/hooks/referrals/types';
import { getFullName } from '@/common/utils/person';
import type { AgentSelectOption } from '$form-fields/AgentAutocomplete/types';
import { findAgentTeamMembership } from '../utils';

type AgentRef = Partial<
  Pick<
    ReferralAgent,
    | 'userId'
    | 'teamId'
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'licenseState'
    | 'avatar'
    | 'displayName'
  >
>;

function toAgentSelectOption(
  agentRef: AgentRef | undefined,
): AgentSelectOption | undefined {
  if (!agentRef?.userId || !agentRef.teamId) return undefined;

  const displayName =
    agentRef.displayName || getFullName(agentRef.firstName, agentRef.lastName);

  if (!displayName) return undefined;

  return {
    id: agentRef.userId,
    teamId: agentRef.teamId,
    userIdentityId: agentRef.userId,
    teamIdentityId: agentRef.teamId,
    displayName,
    firstName: agentRef.firstName ?? '',
    lastName: agentRef.lastName ?? '',
    email: agentRef.email ?? '',
    licenseState: agentRef.licenseState ?? null,
    avatar: agentRef.avatar ?? null,
  };
}

interface AgentRefs {
  referringAgent?: AgentRef;
  transactingAgent?: AgentRef;
}

// A referral's agents can sit outside the session user's submit-as grants
// (e.g. an admin picked a cross-org agent); merge those into the option list
// so the selected agent still renders and validates. Freshly picked agents
// are registered synchronously (the dropdown option already carries the full
// profile); agents on a loaded referral are synthesized from the referral's
// own denormalized profile fields, so no extra fetches are needed. The merged
// list is display-only — permission checks must use the base submit-as list.
export function useAgentsWithSelections(
  agents: AgentSelectOption[],
  { referringAgent, transactingAgent }: AgentRefs,
): {
  agents: AgentSelectOption[];
  registerSelectedAgent: (agent: AgentSelectOption) => void;
} {
  const [selectedAgents, setSelectedAgents] = useState<AgentSelectOption[]>([]);
  const registerSelectedAgent = useCallback((agent: AgentSelectOption) => {
    setSelectedAgents((prev) =>
      findAgentTeamMembership({ userId: agent.id, teamId: agent.teamId }, prev)
        ? prev
        : prev.concat(agent),
    );
  }, []);

  const mergedAgents = useMemo(() => {
    // Registered selections come first: they carry the full profile (avatar,
    // identity ids) while synthesized fallbacks only carry the referral's
    // denormalized fields.
    const fallbackAgents = [
      ...selectedAgents,
      toAgentSelectOption(referringAgent),
      toAgentSelectOption(transactingAgent),
    ];

    const merged = [...agents];
    for (const agent of fallbackAgents) {
      if (
        agent &&
        !findAgentTeamMembership(
          { userId: agent.id, teamId: agent.teamId },
          merged,
        )
      ) {
        merged.push(agent);
      }
    }

    return merged.length === agents.length ? agents : merged;
  }, [agents, selectedAgents, referringAgent, transactingAgent]);

  return { agents: mergedAgents, registerSelectedAgent };
}
